import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const ADMIN_EMAIL = 'colebartimus@getreplyfirst.com';

function Admin() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/', { replace: true });
        return;
      }
      setAuthorized(true);
      fetchData();
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);

    const { data: biz } = await supabase
      .from('businesses')
      .select('id, business_name, owner_name, owner_email, twilio_number, business_phone, notification_phone, is_active, created_at, website_url')
      .order('created_at', { ascending: false });

    if (!biz) { setLoading(false); return; }
    setBusinesses(biz);

    // Fetch stats for each business
    const statsMap = {};
    await Promise.all(biz.map(async (b) => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [callsRes, leadsRes, convoRes] = await Promise.all([
        supabase.from('conversations').select('id', { count: 'exact', head: true }).eq('business_id', b.id).gte('created_at', startOfMonth),
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('business_id', b.id),
        supabase.from('conversations').select('id', { count: 'exact', head: true }).eq('business_id', b.id),
      ]);

      statsMap[b.id] = {
        callsThisMonth: callsRes.count ?? 0,
        totalLeads: leadsRes.count ?? 0,
        totalConvos: convoRes.count ?? 0,
      };
    }));

    setStats(statsMap);
    setLoading(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filtered = businesses.filter(b =>
    !search ||
    (b.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.owner_email || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalActive = businesses.filter(b => b.is_active).length;
  const totalLeadsAll = Object.values(stats).reduce((sum, s) => sum + (s.totalLeads || 0), 0);
  const totalCallsMonth = Object.values(stats).reduce((sum, s) => sum + (s.callsThisMonth || 0), 0);

  if (!authorized) return null;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <header style={{ marginBottom: '28px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <div style={{ width: '28px', height: '28px', background: '#1A56DB', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 2h12v10H9.5L6 14v-2H2V2z" fill="#fff"/></svg>
              </div>
              <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '20px', color: '#0C1A2E' }}>ReplyFirst</span>
              <span style={{ padding: '3px 10px', borderRadius: '999px', backgroundColor: '#F97316', color: '#fff', fontSize: '11px', fontWeight: 700 }}>ADMIN</span>
            </div>
            <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '28px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>
              Master Dashboard
            </h1>
          </div>
          <button
            onClick={() => navigate('/home')}
            style={{ padding: '9px 18px', borderRadius: '999px', border: '1px solid #DDE3F0', backgroundColor: '#fff', color: '#5A6B84', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            ← Back to my dashboard
          </button>
        </header>

        {/* Summary cards */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total Customers', value: businesses.length, icon: '🏢' },
            { label: 'Active This Month', value: totalActive, icon: '✅' },
            { label: 'Calls This Month', value: totalCallsMonth, icon: '📞' },
          ].map(card => (
            <div key={card.label} style={{ backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)' }}>
              <div style={{ fontSize: '22px', marginBottom: '8px' }}>{card.icon}</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#5A6B84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{card.label}</div>
              <div style={{ fontSize: '36px', fontFamily: "'Fraunces', serif", fontWeight: 800, color: '#0C1A2E', letterSpacing: '-1px', lineHeight: 1 }}>
                {loading ? '—' : card.value}
              </div>
            </div>
          ))}
        </section>

        {/* Revenue estimate */}
        <section style={{ backgroundColor: '#0C1A2E', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', border: '1px solid #1A2744', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#7A8EA8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Monthly Recurring Revenue</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '38px', fontWeight: 800, color: '#fff', letterSpacing: '-1px', lineHeight: 1 }}>
              {loading ? '—' : `$${(totalActive * 97).toLocaleString()}`}
            </div>
            <div style={{ fontSize: '13px', color: '#5A6B84', marginTop: '4px' }}>{totalActive} active customers × $97/month</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#7A8EA8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Total Leads Captured</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '38px', fontWeight: 800, color: '#F97316', letterSpacing: '-1px', lineHeight: 1 }}>
              {loading ? '—' : totalLeadsAll}
            </div>
            <div style={{ fontSize: '13px', color: '#5A6B84', marginTop: '4px' }}>across all customers</div>
          </div>
        </section>

        {/* Customer table */}
        <section style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #DDE3F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: '#0C1A2E' }}>
              All Customers
            </h2>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #DDE3F0', fontSize: '13px', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', width: '240px', color: '#0C1A2E' }}
            />
          </div>

          {loading ? (
            <div style={{ padding: '24px', fontSize: '14px', color: '#9AABBE' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '24px', fontSize: '14px', color: '#5A6B84' }}>No customers found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F4F7FF' }}>
                    {['Business', 'Owner', 'ReplyFirst #', 'Status', 'Calls/mo', 'Total Leads', 'Signed Up'].map((h, i) => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#5A6B84', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDE3F0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => {
                    const s = stats[b.id] || {};
                    return (
                      <tr key={b.id} style={{ borderBottom: '1px solid #EEF2FF' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#0C1A2E' }}>{b.business_name || '—'}</div>
                          {b.website_url && (
                            <a href={b.website_url} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#1A56DB', textDecoration: 'none' }}>
                              {b.website_url.replace('https://', '').replace('http://', '').slice(0, 30)}
                            </a>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ color: '#0C1A2E' }}>{b.owner_name || '—'}</div>
                          <div style={{ fontSize: '11px', color: '#9AABBE' }}>{b.owner_email || '—'}</div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#3D4F66', fontFamily: 'monospace', fontSize: '12px' }}>
                          {b.twilio_number || <span style={{ color: '#9AABBE' }}>Not assigned</span>}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                            backgroundColor: b.is_active ? '#ECFDF3' : '#F4F7FF',
                            color: b.is_active ? '#166534' : '#9AABBE',
                            border: `1px solid ${b.is_active ? '#BBF7D0' : '#DDE3F0'}`,
                          }}>
                            {b.is_active ? '● Active' : '○ Trial'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#0C1A2E', fontWeight: 600, textAlign: 'center' }}>
                          {s.callsThisMonth ?? '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#0C1A2E', fontWeight: 600, textAlign: 'center' }}>
                          {s.totalLeads ?? '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#5A6B84', whiteSpace: 'nowrap' }}>
                          {formatDate(b.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </div>
    </div>
  );
}

export default Admin;